import { ConfirmDialog } from "@/components/reusable";
import { parseFile } from "@/services/fileParser";
import { Search, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";

interface ReferenceList {
  id: string;
  name: string;
  usedInColumnIds: string[];
  itemCount: number;
  values: string[];
}

interface SchemaBuilderReferenceListsTabProps {
  referenceLists: ReferenceList[];
  columnNamesById: Record<string, string>;
  onAddReferenceList?: (list: ReferenceList) => void;
  onUpdateReferenceList?: (list: ReferenceList) => void;
  onDeleteReferenceList?: (list: ReferenceList) => void;
  onFileUploaded?: (fileName: string) => void;
}

export function SchemaBuilderReferenceListsTab({
  referenceLists,
  columnNamesById,
  onAddReferenceList,
  onUpdateReferenceList,
  onDeleteReferenceList,
  onFileUploaded,
}: SchemaBuilderReferenceListsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [listToDelete, setListToDelete] = useState<ReferenceList | null>(null);

  const filteredReferenceLists = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return referenceLists;

    return referenceLists.filter((list) => {
      return (
        list.name.toLowerCase().includes(query) ||
        list.usedInColumnIds.some((id) =>
          (columnNamesById[id] ?? id).toLowerCase().includes(query)
        ) ||
        list.values.some((value) => value.toLowerCase().includes(query))
      );
    });
  }, [referenceLists, searchQuery, columnNamesById]);

  function handleAddList() {
    const newList: ReferenceList = {
      id: `reference-list-${Date.now()}`,
      name: "",
      usedInColumnIds: [],
      itemCount: 0,
      values: [],
    };

    onAddReferenceList?.(newList);
  }

  function handleConfirmDelete() {
    if (!listToDelete) return;

    onDeleteReferenceList?.(listToDelete);
    setListToDelete(null);
  }

  return (
    <>
      <p className="mb-6 text-sm text-[var(--color-text-primary)]">
        All reference lists that have been utilized in your schema can be viewed and edited here.
      </p>

      <div className="mb-4 flex items-center justify-between">
        <div className="flex h-9 w-[240px] shrink-0 items-center gap-2 rounded-md border border-[var(--color-border)] bg-white px-3">
          <Search className="h-4 w-4 text-[var(--color-text-tertiary)]" />
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search lists..."
            className="w-full text-sm outline-none placeholder:text-[var(--color-text-tertiary)]"
          />
        </div>

        <button
          type="button"
          onClick={handleAddList}
          className="rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)]"
        >
          Add List
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {filteredReferenceLists.map((list) => (
          <ReferenceListCard
            key={list.id}
            list={list}
            columnNamesById={columnNamesById}
            onUpdateReferenceList={onUpdateReferenceList}
            onDelete={() => setListToDelete(list)}
            onFileUploaded={onFileUploaded}
          />
        ))}

        {filteredReferenceLists.length === 0 && (
          <div className="rounded-md border border-[var(--color-border-light)] bg-white p-6 text-sm text-[var(--color-text-secondary)]">
            No reference lists match the current search.
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(listToDelete)}
        onOpenChange={(open) => {
          if (!open) setListToDelete(null);
        }}
        title="Delete reference list?"
        description={
          <>
            This will remove{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {listToDelete?.name}
            </span>{" "}
            from this schema.
            {listToDelete && listToDelete.usedInColumnIds.length > 0 && (
              <>
                {" "}
                <span className="mt-2 block text-amber-600">
                  This list is used in{" "}
                  {listToDelete.usedInColumnIds.length === 1
                    ? "1 column"
                    : `${listToDelete.usedInColumnIds.length} columns`}
                  : {listToDelete.usedInColumnIds.map((id) => columnNamesById[id] ?? id).join(", ")}
                  . Deleting it will flag{" "}
                  {listToDelete.usedInColumnIds.length === 1 ? "that column" : "those columns"} as
                  conflicted.
                </span>
              </>
            )}
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

function ReferenceListCard({
  list,
  columnNamesById,
  onUpdateReferenceList,
  onDelete,
  onFileUploaded,
}: {
  list: ReferenceList;
  columnNamesById: Record<string, string>;
  onUpdateReferenceList?: (list: ReferenceList) => void;
  onDelete: () => void;
  onFileUploaded?: (fileName: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(list.values.length === 0);
  const [isEditingManualEntry, setIsEditingManualEntry] = useState(list.values.length === 0);
  const [draftName, setDraftName] = useState(list.name);
  const [draftValues, setDraftValues] = useState(list.values);
  const [manualEntryValue, setManualEntryValue] = useState(list.values.join(", "));
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizedDraftValues = draftValues.map((value) => value.trim()).filter(Boolean);
  const normalizedOriginalValues = list.values.map((value) => value.trim()).filter(Boolean);

  const hasChanges =
    draftName.trim() !== list.name ||
    normalizedDraftValues.join("|") !== normalizedOriginalValues.join("|");

  function handleEdit() {
    setIsEditing(true);
    setIsEditingManualEntry(false);
    setDraftName(list.name);
    setDraftValues(list.values);
    setManualEntryValue(list.values.join(", "));
  }

  function handleDiscardChanges() {
    if (list.values.length === 0 && list.name === "") {
      onDelete();
      return;
    }

    setIsEditing(false);
    setIsEditingManualEntry(false);
    setDraftName(list.name);
    setDraftValues(list.values);
    setManualEntryValue(list.values.join(", "));
  }

  function handleDoneManualEntry() {
    const values = manualEntryValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    setDraftValues(values);
    setIsEditingManualEntry(false);
  }

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleUploadFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = "";
    setUploadError(null);

    try {
      const sheets = await parseFile(file);
      const sheet = sheets[0];
      if (!sheet) {
        setUploadError("No data found in file.");
        return;
      }

      // parseFile treats row 0 as a header — use it as the list name, and every
      // subsequent row as a value.
      const values = sheet.rows
        .map((row) => String(Object.values(row)[0] ?? ""))
        .map((v) => v.trim())
        .filter(Boolean);

      if (values.length === 0) {
        setUploadError("File appears to be empty.");
        return;
      }

      setDraftName(sheet.headers[0]?.trim() || file.name.replace(/\.[^/.]+$/, ""));
      setDraftValues(values);
      setManualEntryValue(values.join(", "));
      onFileUploaded?.(file.name);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Failed to parse file.");
    }
  }

  function handleSaveChanges() {
    const savedValues = draftValues.map((value) => value.trim()).filter(Boolean);
    const resolvedName = draftName.trim();

    onUpdateReferenceList?.({
      ...list,
      name: resolvedName,
      itemCount: savedValues.length,
      values: savedValues,
    });

    setIsEditing(false);
    setIsEditingManualEntry(false);
  }

  const valuesToDisplay = isEditing ? draftValues : list.values;

  return (
    <div className="rounded-md border border-[var(--color-border-light)] border-l-4 border-l-blue-600 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {isEditing
              ? draftName || <span className="text-[var(--color-text-tertiary)]">New List</span>
              : list.name}
          </h3>

          <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
            {(() => {
              const names = list.usedInColumnIds.map((id) => columnNamesById[id] ?? id);
              return `used in [${names.length}] column${names.length !== 1 ? "s" : ""}${names.length > 0 ? `: ${names.join(", ")}` : ""}`;
            })()}
          </p>
        </div>

        <span className="text-xs text-[var(--color-text-secondary)]">
          {valuesToDisplay.length} list items
        </span>
      </div>

      {isEditing && (
        <div className="mb-4 space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={handleUploadFile}
          />

          <div className="grid grid-cols-[1fr_1fr] gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold">List Name</label>
              <input
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                placeholder="List name"
                className="h-9 w-full rounded border border-[var(--color-border)] px-2 text-xs"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold">Upload List</label>
              <button
                type="button"
                onClick={handleUploadClick}
                className="flex h-9 w-full items-center justify-center rounded border border-[var(--color-border)] text-[var(--color-action-primary)]"
              >
                <Upload className="h-4 w-4" />
              </button>
            </div>
          </div>

          {uploadError && <p className="text-xs text-red-500">{uploadError}</p>}

          {!isEditingManualEntry ? (
            <button
              type="button"
              onClick={() => setIsEditingManualEntry(true)}
              className="text-xs font-medium text-blue-600"
            >
              Edit list items manually
            </button>
          ) : (
            <div>
              <label className="mb-1 block text-xs font-semibold">
                Manual Entry{" "}
                <span className="font-normal text-[var(--color-text-tertiary)]">
                  (Case-sensitive, separate values by comma)
                </span>
              </label>

              <textarea
                value={manualEntryValue}
                onChange={(event) => setManualEntryValue(event.target.value)}
                className="h-24 w-full resize-none rounded border border-[var(--color-border)] px-3 py-2 text-xs outline-none focus:border-[var(--color-action-primary)]"
              />

              <div className="mt-2 flex justify-end gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingManualEntry(false);
                    setManualEntryValue(draftValues.join(", "));
                  }}
                  className="text-red-500"
                >
                  Cancel
                </button>

                <button type="button" onClick={handleDoneManualEntry} className="text-blue-600">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-6 max-h-64 overflow-y-auto">
        <div className="flex flex-wrap gap-2">
          {valuesToDisplay.map((value) => (
            <span
              key={value}
              className="rounded border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-700"
            >
              {value}
            </span>
          ))}
        </div>
      </div>

      {isEditing ? (
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleDiscardChanges}
            className="rounded border border-[var(--color-border)] px-4 py-1.5 text-xs text-[var(--color-text-primary)]"
          >
            Discard Changes
          </button>

          <button
            type="button"
            onClick={handleSaveChanges}
            disabled={
              !draftName.trim() || draftValues.length === 0 || isEditingManualEntry || !hasChanges
            }
            className="rounded bg-[var(--color-action-primary)] px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
          >
            Save Changes
          </button>
        </div>
      ) : (
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleEdit}
            className="rounded border border-[var(--color-border)] px-4 py-1.5 text-xs text-[var(--color-text-primary)]"
          >
            Edit List
          </button>

          <button
            type="button"
            onClick={onDelete}
            className="rounded border border-red-300 px-4 py-1.5 text-xs text-red-500"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
